/**
 * Creates TableDropdownOption objects from a simple array of strings
 * @param items - Array of strings
 * @param idGenerator - Optional function to generate stable IDs for each item
 * @returns Array of TableDropdownOption objects
 */
export const createOptionsFromStrings = (
  items: string[],
  idGenerator?: (item: string, index: number) => string
): Array<{ id: string; label: string; value: string }> => {
  return items.map((item, index) => ({
    id: idGenerator ? idGenerator(item, index) : generateUniqueId(`string_${sanitizeString(item)}_`),
    label: item,
    value: item,
  }));
};

/**
 * Sanitizes a string by removing or replacing special characters for safe ID generation
 * @param str - String to sanitize
 * @returns Sanitized string safe for ID generation
 */
const sanitizeString = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9\s]/g, '_') // Replace special characters (except letters, numbers, and spaces) with underscores
    .replace(/_+/g, '_') // Replace multiple consecutive underscores with single underscore
    .replace(/^_|_$/g, ''); // Remove leading and trailing underscores
  // Note: Removed .toLowerCase() to preserve original case
};

/**
 * Creates TableDropdownOption objects from an array of objects with custom key mapping
 * @param items - Array of objects
 * @param idKey - Key to use for the id field
 * @param labelKey - Key to use for the label field
 * @param valueKey - Key to use for the value field (optional, defaults to idKey)
 * @returns Array of TableDropdownOption objects
 */
export const createOptionsFromObjects = <T extends Record<string, any>>(
  items: T[],
  idKey: keyof T,
  labelKey: keyof T,
  valueKey?: keyof T
): Array<{ id: string | number; label: string; value: any }> => {
  return items.map((item) => ({
    id: item[idKey],
    label: String(item[labelKey]),
    value: valueKey ? item[valueKey] : item[idKey],
  }));
};

/**
 * Filters options based on search term with case-insensitive matching
 * @param options - Array of options to filter
 * @param searchTerm - Search term to filter by
 * @param searchKeys - Keys to search in (defaults to ['label'])
 * @returns Filtered array of options
 */
export const filterOptions = <T extends { label: string; [key: string]: any }>(
  options: T[],
  searchTerm: string,
  searchKeys: (keyof T)[] = ['label']
): T[] => {
  if (!searchTerm.trim()) return options;

  const searchLower = searchTerm.toLowerCase();
  return options.filter((option) =>
    searchKeys.some((key) => {
      const value = option[key];
      return String(value).toLowerCase().includes(searchLower);
    })
  );
};

/**
 * Debounces a function call
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: any;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Generates a unique ID for options that don't have one
 * @param prefix - Prefix for the generated ID
 * @returns Unique ID string
 */
export const generateUniqueId = (prefix = 'option'): string => {
  return `${prefix}_${Math.random().toString(36).substring(2, 11)}`;
};
